import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { Form } from "react-bootstrap";
import CustomDataTable from "../../components/CustomDataTable";
import Helpers from "../../components/Helpers";
import { utils, writeFile } from "xlsx";
import ActivityLogApi from "../../api/activityLogApi";
import {
  actionBadgeClass,
  activitySummaryText,
  ChangedDataView,
  displayActor,
  displayRole,
  eventLabel,
  ModuleLinkCell,
} from "../../utils/activityLogFormat";
import "../../assets/css/LogReport.css";

const MODULES = [
  "",
  "auth",
  "retreat",
  "couple",
  "parish",
  "priest",
  "role",
  "service",
  "talk",
  "committee",
  "email",
  "help",
  "waitlist",
  "registration",
];

const ACTIONS = [
  "",
  "Add",
  "Edit",
  "Delete",
  "Login",
  "Logout",
  "Assign",
  "Access",
  "Notify",
  "Lifecycle",
  "Publish",
  "Close",
];

function formatTimeParts(createdTime) {
  const full = Helpers.convertUtcToTimezone(
    createdTime,
    "MM-DD-YYYY hh:mm a"
  );
  if (!full || full === "Invalid date") {
    return { date: "—", clock: "" };
  }
  const [date, ...rest] = String(full).split(" ");
  return { date, clock: rest.join(" ") };
}

const LogReport = () => {
  const [LoadData, setLoadData] = useState(false);
  const [reportList, setReportList] = useState([]);
  const [total, setTotal] = useState(0);
  const [searchKey, setSearchKey] = useState("");
  const [moduleFilter, setModuleFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const api = new ActivityLogApi();

  const hasActiveFilters = Boolean(
    searchKey || moduleFilter || actionFilter || dateFrom || dateTo
  );

  const getAllLogReport = useCallback(
    async (pageNo = 1, size = 20, searchKeyword = null, sortBy = {}) => {
      setLoadData(true);
      const d = {
        pageNo,
        size,
        searchKeyword: searchKeyword !== null ? searchKeyword : searchKey,
        module: moduleFilter || undefined,
        action: actionFilter || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        export: false,
      };
      if (sortBy?.sortColumn) d.sortBy = sortBy;
      try {
        const res = await api.list(d);
        if (res.success) {
          setReportList(res.data || []);
          setTotal(res.totalRecords || 0);
        } else {
          toast.error(res.msg || "Failed to load activity log");
        }
      } catch (error) {
        toast.error(error?.toString?.() || "Failed to load activity log");
      } finally {
        setLoadData(false);
      }
    },
    [searchKey, moduleFilter, actionFilter, dateFrom, dateTo]
  );

  useEffect(() => {
    getAllLogReport();
  }, [moduleFilter, actionFilter, dateFrom, dateTo]);

  const SearchData = (event) => {
    const key = event.target.value;
    setSearchKey(key);
    getAllLogReport(1, 20, key);
  };

  const clearFilters = () => {
    setSearchKey("");
    setModuleFilter("");
    setActionFilter("");
    setDateFrom("");
    setDateTo("");
    getAllLogReport(1, 20, "");
  };

  const columns = [
    {
      id: 1,
      name: "Time",
      selector: (row) => row.createdTime,
      cell: (row) => {
        const { date, clock } = formatTimeParts(row.createdTime);
        return (
          <div className="log-report-time">
            <span className="log-report-time-date">{date}</span>
            {clock ? <span className="log-report-time-clock">{clock}</span> : null}
          </div>
        );
      },
      sortable: true,
      width: "130px",
    },
    {
      id: 2,
      name: "User",
      selector: (row) => displayActor(row),
      cell: (row) => (
        <div className="log-report-user">
          <span className="log-report-user-name">{displayActor(row)}</span>
          <span className="log-report-role-chip">{displayRole(row)}</span>
        </div>
      ),
      sortable: true,
      width: "170px",
    },
    {
      id: 3,
      name: "Action",
      selector: (row) => row.action,
      cell: (row) => (
        <span className={actionBadgeClass(row.action)}>
          {row.action || "—"}
        </span>
      ),
      sortable: true,
      width: "130px",
      grow: 0,
      wrap: false,
      style: { whiteSpace: "nowrap" },
    },
    {
      id: 4,
      name: "Module",
      selector: (row) => row.module || row.moduleLink,
      cell: (row) => <ModuleLinkCell row={row} />,
      sortable: true,
      width: "120px",
    },
    {
      id: 5,
      name: "Event",
      selector: (row) => row.eventType,
      cell: (row) => {
        const label = eventLabel(row.eventType);
        return label ? (
          <span className="log-report-event">{label}</span>
        ) : (
          <span className="log-report-event-empty">—</span>
        );
      },
      sortable: true,
      width: "180px",
    },
    {
      id: 6,
      name: "Changed data",
      selector: (row) =>
        row.changedDataParsed?.summary || row.changedData || "",
      cell: (row) => (
        <div className="log-report-changed-cell">
          <ChangedDataView
            raw={row.changedData}
            parsed={row.changedDataParsed}
          />
        </div>
      ),
      grow: 3,
      wrap: true,
      allowOverflow: true,
      minWidth: "360px",
      style: {
        whiteSpace: "normal",
        wordBreak: "break-word",
        overflow: "visible",
        paddingTop: "12px",
        paddingBottom: "12px",
      },
    },
  ];

  const handleExport = async () => {
    try {
      const res = await api.list({
        searchKeyword: searchKey,
        module: moduleFilter || undefined,
        action: actionFilter || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        export: true,
      });
      if (!res.success) {
        toast.error(res.msg || "Export failed");
        return;
      }
      const exportData = (res.data || []).map((element) => ({
        Time: Helpers.convertUtcToTimezone(
          element.createdTime,
          "MM-DD-YYYY hh:mm a"
        ),
        User: displayActor(element),
        Role: displayRole(element),
        Action: element.action || "",
        Module: element.module || "",
        Event: eventLabel(element.eventType),
        Summary: activitySummaryText(
          element.changedData,
          element.changedDataParsed
        ),
      }));
      const headings = [
        ["Time", "User", "Role", "Action", "Module", "Event", "Summary"],
      ];
      const wb = utils.book_new();
      const ws = utils.json_to_sheet([]);
      utils.sheet_add_aoa(ws, headings);
      utils.sheet_add_json(ws, exportData, { origin: "A2", skipHeader: true });
      utils.book_append_sheet(wb, ws, "Data");
      writeFile(wb, `Log Report.xlsx`);
    } catch (error) {
      toast.error(error?.toString?.() || "Export failed");
    }
  };

  return (
    <div className="row log-report-page">
      <div className="col-lg-12 d-flex flex-column">
        <div className="card custom-card log-report-shell border-0">
          <div className="card-body">
            <div className="log-report-inset">
              <div className="log-report-header">
                <div>
                  <p className="log-report-kicker">Activity</p>
                  <div className="log-report-title-row">
                    <h1 className="log-report-title">Log Report</h1>
                    <span className="log-report-count" title="Matching entries">
                      {total}
                    </span>
                  </div>
                  <p className="log-report-subtitle">
                    Significant actions only. Entries older than 4 months are
                    removed automatically.
                  </p>
                </div>
                <div className="log-report-header-actions">
                  {hasActiveFilters ? (
                    <button
                      type="button"
                      className="log-report-clear"
                      onClick={clearFilters}
                    >
                      Clear filters
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="log-report-export"
                    onClick={handleExport}
                  >
                    <i className="mdi mdi-download" aria-hidden="true" />
                    Export
                  </button>
                </div>
              </div>

              <div className="log-report-toolbar">
                <div className="log-report-field">
                  <label className="log-report-label">Module</label>
                  <Form.Select
                    size="sm"
                    value={moduleFilter}
                    onChange={(e) => setModuleFilter(e.target.value)}
                  >
                    <option value="">All modules</option>
                    {MODULES.filter(Boolean).map((m) => (
                      <option key={m} value={m}>
                        {m.charAt(0).toUpperCase() + m.slice(1)}
                      </option>
                    ))}
                  </Form.Select>
                </div>
                <div className="log-report-field">
                  <label className="log-report-label">Action</label>
                  <Form.Select
                    size="sm"
                    value={actionFilter}
                    onChange={(e) => setActionFilter(e.target.value)}
                  >
                    <option value="">All actions</option>
                    {ACTIONS.filter(Boolean).map((a) => (
                      <option key={a} value={a}>
                        {a}
                      </option>
                    ))}
                  </Form.Select>
                </div>
                <div className="log-report-field">
                  <label className="log-report-label">From</label>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </div>
                <div className="log-report-field">
                  <label className="log-report-label">To</label>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </div>
                <div className="log-report-field log-report-field--search">
                  <label className="log-report-label">Search</label>
                  <div className="log-report-search input-group">
                    <input
                      onChange={SearchData}
                      value={searchKey}
                      type="text"
                      className="form-control searchData"
                      placeholder="User, module, event, details…"
                    />
                    <button className="log-report-search-btn" type="button">
                      <i className="icon-search" />
                    </button>
                  </div>
                </div>
              </div>

              {reportList.length === 0 && !LoadData ? (
                <div className="log-report-empty">
                  <p className="log-report-empty-title">No activity found</p>
                  <p className="log-report-empty-text">
                    Try a different date range or clear your filters.
                  </p>
                </div>
              ) : null}
            </div>

            <div className="log-report-table-wrap">
              <CustomDataTable
                columns={columns}
                data={reportList}
                loading={LoadData}
                total={total}
                countPerPage={20}
                pagination={true}
                isServerSide={true}
                sortServer={true}
                dataFetchApi={getAllLogReport}
                highlightOnHover={true}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogReport;
